<template>
  <div>
    <Dialog v-model:visible="visible" modal :closable="false" header="Import Documents" :style="{ width: '60vw' }" :breakpoints="{ '1199px': '75vw', '575px': '90vw' }">
      <div v-if="!uploaded">
        <small>Select an export file to import the KB documents from</small>
        <FileUpload
          name="uploader"
          custom-upload
          :multiple="false"
          accept="application/gzip, application/json , application/x-gzip"
          :max-file-size="10000000"
          :pt="{
            thumbnail: { src: '/img/icon-file.svg' },
          }"
          @uploader="onFileUpload($event)"
        >
          <template #empty>
            <p>Drag and drop file here to upload.</p>
          </template>
        </FileUpload>
      </div>
      <div v-if="contentReady" class="mt-4">
        <div class="text-xl font-bold text-red-600">Attention! Import will override existing documents if their names match.</div>
      </div>
      <div class="mt-4">
        <small class="block min-h-6">{{ progressText }} </small>
        <ProgressBar :mode="progressMode" style="height: 6px"></ProgressBar>
      </div>
      <div v-if="errorMessage" class="my-4 w-full">
        <Message :closable="false" severity="error">{{ errorMessage }}</Message>
      </div>
      <template #footer>
        <Button label="Cancel" :disabled="showProgress" severity="secondary" autofocus @click="visible = false" />
        <Button label="Import" :disabled="!contentReady || showProgress" severity="primary" autofocus @click="importKbDocuments" />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
  import { ref, computed, nextTick } from 'vue';
  import Dialog from 'primevue/dialog';
  import Button from 'primevue/button';
  import ProgressBar from 'primevue/progressbar';
  import FileUpload from 'primevue/fileupload';
  import Message from 'primevue/message';
  import { useKbStore } from '@/store/KbStore';
  import { useToast } from 'vue-toastification';

  const kbStore = useKbStore();
  const toast = useToast();

  const visible = ref(false);
  const uploaded = ref(false);
  const showProgress = ref(false);
  const progressMode = computed(() => (showProgress.value ? 'indeterminate' : ''));
  const progressText = ref('');
  const contentReady = ref(false);
  const errorMessage = ref(null);

  let exportData;
  let exportDataDeflated = true;

  function start() {
    uploaded.value = false;
    contentReady.value = false;
    progressText.value = '';
    showProgress.value = false;
    errorMessage.value = null;
    visible.value = true;
  }

  async function onFileUpload(event) {
    const file = event.files[0];
    uploaded.value = true;
    showProgress.value = true;
    progressText.value = 'Processing file...';
    try {
      // Check if it's a gzipped file
      if (file.type === 'application/gzip' || file.name.endsWith('.gz')) {
        try {
          // For gzipped files, use DecompressionStream API
          exportData = await file.arrayBuffer();
          exportDataDeflated = true;
          /*
          const blob = new Blob([arrayBuffer]);
          const decompressedStream = blob.stream().pipeThrough(new DecompressionStream('gzip'));
          const decompressedBlob = await new Response(decompressedStream).blob();
          exportData = await decompressedBlob.text();
          */
        } catch (error) {
          errorMessage.value = `Error decompressing gzip file: ${error.message}`;
          showProgress.value = false;
          return;
        }
      } else {
        // For regular JSON files
        exportData = await file.text();
        exportDataDeflated = false;
      }
      showProgress.value = false;
      validateArchiveContent();
    } catch (error) {
      errorMessage.value = `Error reading file: ${error.message}`;
      showProgress.value = false;
    }
  }

  function validateArchiveContent() {
    contentReady.value = true;
  }

  async function importKbDocuments() {
    progressText.value = 'Importing documents ...';
    showProgress.value = true;

    const res = await kbStore.importKbDocuments(exportData, { defalted: exportDataDeflated });

    progressText.value = '';
    showProgress.value = false;
    visible.value = false;

    if (!res.success) {
      toast.add({ severity: 'error', summary: 'Error', detail: `Error importing documents: ${res.error}`, life: 3000 });
      return;
    }

    await nextTick();
  }

  defineExpose({ start });
</script>
